import { useQuery } from "@apollo/react-hooks";
import {
    QUERY_LIST_STORE_APPS,
    QUERY_SYSTEM_APP_LIST,
} from "apollo/module-operations/app";
import _ from "lodash";
import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { useStore, useUser } from "@/hooks";
import { Store } from "@/types";
import { getPrismicRepoUrl, getPrismicDocument } from "@/utils";
import {
    getSellTreezConfigBaseUrlFromEndpoint,
    getSellTreezStoreConfig,
} from "data/sell-treez/util/sellTreezStoreConfig";

type ExternalUrls = {
    storeURL: string | null;
    prismicURL: string | null;
    prismicHomeURL: string | null;
    prismicPromoURL: string | null;
    prismicNavigationURL: string | null;
};

type StoreAppNavOption = {
    value: string;
    label: string;
    app: Record<string, unknown> & {
        handler?: string;
        name?: string;
        status?: boolean;
    };
};

type TreezDispensaryOption = {
    value: string;
    store_id: string;
    label: string;
    /** JSON from `${sellTreezBase}/config/:storeShortName` when fetch succeeds. */
    config?: unknown;
    configError?: string | null;
};

type ProviderOption = {
    value: string;
    label: string;
};

type TreezSellLocation = {
    id?: string;
    name?: string;
    inventoryType?: string;
};

type TreezInventoryOption = {
    label: string;
    value: string | number;
};

type TreezStoreContextById = {
    storeId: string;
    mso_org_id?: string;
    mso_store_entity_id?: string;
    configError?: string | null;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    locations?: TreezInventoryOption[];
};

type StoreContextProps = {
    store: Store | undefined;
    loading: boolean;
    externalUrls: ExternalUrls | null;
    externalLoading: boolean;
    /** Catalog from `listApps` (system-wide app definitions). */
    systemApps: unknown[];
    systemAppsLoading: boolean;
    refetchSystemApps: () => Promise<unknown>;
    /** Apps installed for the selected account/store. */
    installedApps: StoreAppNavOption["app"][];
    /** Options for installed-app tabs / selectors `{ value, label, app }`. */
    storeApps: StoreAppNavOption[];
    storeAppsLoading: boolean;
    refetchStoreApps: () => Promise<unknown>;
    treezDispensaries: TreezDispensaryOption[];
    /** True while Sell Treez config requests are in flight for current credentials. */
    treezSellConfigsLoading: boolean;
    /** Set when OpenSearch endpoint env is missing/invalid (config fetch is skipped). */
    treezSellConfigUnavailableReason: string | null;
    providers: ProviderOption[];
    getTreezStoreContextByStoreId: (
        storeId: string,
    ) => TreezStoreContextById | undefined;
};

const StoreContext = createContext<StoreContextProps | undefined>(undefined);
StoreContext.displayName = "StoreContext";

/** Stable refs so `useEffect` deps on Treez lists do not change every render. */
const EMPTY_TREEZ_DISPENSARIES: TreezDispensaryOption[] = [];
const EMPTY_PROVIDERS: ProviderOption[] = [];

const mapTreezLocationsToOptions = (
    locations: unknown,
): TreezInventoryOption[] => {
    if (!Array.isArray(locations)) {
        return [];
    }

    return locations
        .map((loc): TreezInventoryOption | null => {
            if (!loc || typeof loc !== "object") return null;
            const row = loc as TreezSellLocation;
            if (row.id == null) return null;

            return {
                label: row.name,
                value: row.id,
            };
        })
        .filter((option): option is TreezInventoryOption => option != null);
};

const StoreProvider: FC<PropsWithChildren> = ({ children }) => {
    const { loading: userLoading, accountId, storeId } = useUser();
    const skipApps = userLoading || !accountId || !storeId;

    const { store, loading } = useStore();
    const [externalUrls, setExternalUrls] = useState<ExternalUrls | null>(null);
    const [externalLoading, setExternalLoading] = useState(false);

    const {
        data: systemAppsData,
        loading: systemAppsLoading,
        refetch: refetchSystemApps,
    } = useQuery(QUERY_SYSTEM_APP_LIST, {
        skip: skipApps,
        notifyOnNetworkStatusChange: true,
    });

    const {
        data: storeAppsData,
        loading: storeAppsLoading,
        refetch: refetchStoreApps,
    } = useQuery(QUERY_LIST_STORE_APPS, {
        skip: skipApps,
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        variables: {
            account_id: accountId as string,
            store_id: storeId as string,
        },
    });

    const systemApps = useMemo(
        () => systemAppsData?.listApps ?? [],
        [systemAppsData],
    );

    const { installedApps, storeApps, treezDispensariesBase, providers } =
        useMemo(() => {
            const empty = {
                installedApps: [] as StoreAppNavOption["app"][],
                storeApps: [] as StoreAppNavOption[],
                treezDispensariesBase: EMPTY_TREEZ_DISPENSARIES,
                providers: EMPTY_PROVIDERS,
            };

            if (!storeAppsData?.listStoreApps) {
                return empty;
            }

            const ops: StoreAppNavOption[] = [];
            const activeApps: StoreAppNavOption["app"][] = [];
            let treezDispensariesLocal: TreezDispensaryOption[] =
                EMPTY_TREEZ_DISPENSARIES;
            let providersLocal: ProviderOption[] = EMPTY_PROVIDERS;

            const apps = storeAppsData.listStoreApps;

            _.forEach(apps, app => {
                if (app.status) {
                    const name =
                        app.name === "Sticky" ? "Treez Loyalty" : app.name;
                    const updatedApp = { ...app, name };
                    activeApps.push(updatedApp);
                    ops.push({ value: app.id, label: name, app: updatedApp });
                }

                if (app.handler === "treez") {
                    const dispensaries: TreezDispensaryOption[] = [];
                    _.forEach(app.credentials, cred => {
                        dispensaries.push({
                            value: `${cred.store_id}|${cred.dispensary_name}`,
                            store_id: cred.dispensary_name,
                            label: cred.store_name,
                        });
                    });
                    treezDispensariesLocal =
                        dispensaries.length > 0
                            ? dispensaries
                            : EMPTY_TREEZ_DISPENSARIES;
                    providersLocal =
                        dispensaries.length > 0
                            ? dispensaries.map(a => ({
                                  value: a.store_id,
                                  label: a.label,
                              }))
                            : EMPTY_PROVIDERS;
                }
            });

            return {
                installedApps: activeApps,
                storeApps: ops,
                treezDispensariesBase: treezDispensariesLocal,
                providers: providersLocal,
            };
        }, [storeAppsData]);

    const [treezSellConfigByShortName, setTreezSellConfigByShortName] =
        useState<Record<string, { data?: unknown; error: string | null }>>({});

    const [treezSellConfigsLoading, setTreezSellConfigsLoading] =
        useState(false);

    const [
        treezSellConfigUnavailableReason,
        setTreezSellConfigUnavailableReason,
    ] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const shortNames = treezDispensariesBase.map(d => d.store_id);

        if (shortNames.length === 0) {
            setTreezSellConfigByShortName({});
            setTreezSellConfigsLoading(false);
            setTreezSellConfigUnavailableReason(null);
            return;
        }

        if (
            !getSellTreezConfigBaseUrlFromEndpoint(
                process.env.REACT_APP_OPEN_SEARCH_ENDPOINT,
            )
        ) {
            setTreezSellConfigByShortName({});
            setTreezSellConfigsLoading(false);
            setTreezSellConfigUnavailableReason(
                "REACT_APP_OPEN_SEARCH_ENDPOINT is not set or invalid",
            );
            return;
        }

        setTreezSellConfigUnavailableReason(null);
        setTreezSellConfigsLoading(true);
        setTreezSellConfigByShortName({});

        void Promise.allSettled(
            shortNames.map(shortName => getSellTreezStoreConfig(shortName)),
        ).then(results => {
            if (cancelled) {
                return;
            }

            const next: Record<
                string,
                { data?: unknown; error: string | null }
            > = {};

            shortNames.forEach((shortName, index) => {
                const settled = results[index];
                if (settled.status === "fulfilled") {
                    next[shortName] = {
                        data: settled.value,
                        error: null,
                    };
                } else {
                    const reason = settled.reason as Error | undefined;
                    next[shortName] = {
                        error:
                            reason?.message ??
                            String(settled.reason ?? "Unknown error"),
                    };
                }
            });

            setTreezSellConfigByShortName(next);
            setTreezSellConfigsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [treezDispensariesBase]);

    const treezDispensaries = useMemo(
        () =>
            treezDispensariesBase.map(d => {
                const entry = treezSellConfigByShortName[d.store_id];
                return {
                    ...d,
                    config: entry?.data,
                    configError: entry?.error ?? null,
                };
            }),
        [treezDispensariesBase, treezSellConfigByShortName],
    );

    const getTreezStoreContextByStoreId = useCallback(
        (targetStoreId: string): TreezStoreContextById | undefined => {
            const key = String(targetStoreId ?? "").trim();
            if (!key) return undefined;

            const row = treezDispensaries.find(
                d => String(d.store_id).trim() === key,
            );
            if (!row) return undefined;

            const config =
                row.config && typeof row.config === "object"
                    ? (row.config as Record<string, unknown>)
                    : {};

            const locations = mapTreezLocationsToOptions(config.locations);

            return {
                storeId: row.store_id,
                mso_org_id: config.mso_org_id as string | undefined,
                mso_store_entity_id: config.mso_store_entity_id as
                    | string
                    | undefined,
                configError: row.configError ?? null,
                latitude: config.latitude as number | undefined,
                longitude: config.longitude as number | undefined,
                timezone: config.dispensary_time_zone as string | undefined,
                locations,
            };
        },
        [treezDispensaries],
    );

    useEffect(() => {
        let cancelled = false;

        const resolveExternalUrls = async (
            currentStore: Store | undefined,
        ): Promise<ExternalUrls> => {
            const storeURL = currentStore?.domain ?? null;
            const endpoint = currentStore?.prismic_api_endpoint ?? null;
            const prismicURL = endpoint ? getPrismicRepoUrl(endpoint) : null;

            if (!endpoint || !prismicURL) {
                return {
                    storeURL,
                    prismicURL,
                    prismicHomeURL: null,
                    prismicPromoURL: null,
                    prismicNavigationURL: null,
                };
            }

            try {
                const [homeId, promoId, navigationId] = await Promise.all([
                    getPrismicDocument({
                        repoUrl: endpoint,
                        documentType: "homepage",
                    }),
                    getPrismicDocument({
                        repoUrl: endpoint,
                        documentType: "promo_carousel",
                    }),
                    getPrismicDocument({
                        repoUrl: endpoint,
                        documentType: "navigation",
                    }),
                ]);

                return {
                    storeURL,
                    prismicURL,
                    prismicHomeURL: homeId
                        ? `${prismicURL}builder/pages/${homeId}`
                        : null,
                    prismicPromoURL: promoId
                        ? `${prismicURL}builder/pages/${promoId}`
                        : null,
                    prismicNavigationURL: navigationId
                        ? `${prismicURL}builder/pages/${navigationId}`
                        : null,
                };
            } catch (error) {
                console.error("Error fetching Prismic documents", error);
                return {
                    storeURL,
                    prismicURL,
                    prismicHomeURL: null,
                    prismicPromoURL: null,
                    prismicNavigationURL: null,
                };
            }
        };

        const run = async () => {
            if (!store) {
                setExternalUrls(null);
                return;
            }

            setExternalLoading(true);

            try {
                const urls = await resolveExternalUrls(store);

                if (!cancelled) {
                    setExternalUrls(urls);
                }
            } finally {
                if (!cancelled) {
                    setExternalLoading(false);
                }
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [store]);

    const value = useMemo(
        () => ({
            store,
            loading,
            externalUrls,
            externalLoading,
            systemApps,
            systemAppsLoading,
            refetchSystemApps,
            installedApps,
            storeApps,
            storeAppsLoading,
            refetchStoreApps,
            treezDispensaries,
            treezSellConfigsLoading,
            treezSellConfigUnavailableReason,
            providers,
            getTreezStoreContextByStoreId,
        }),
        [
            store,
            loading,
            externalUrls,
            externalLoading,
            systemApps,
            systemAppsLoading,
            refetchSystemApps,
            installedApps,
            storeApps,
            storeAppsLoading,
            refetchStoreApps,
            treezDispensaries,
            treezSellConfigsLoading,
            treezSellConfigUnavailableReason,
            providers,
            getTreezStoreContextByStoreId,
        ],
    );

    return (
        <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
    );
};

const useStoreContext = () => {
    const context = useContext(StoreContext);

    if (!context) {
        throw new Error("useStoreContext must be used withing StoreProvider");
    }

    return context;
};

export { StoreProvider, useStoreContext };
